import {BehaviorCountType} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';
import {DatetimeOperator} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {BaseTimeUnit, DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {
  SerializedBehaviorWindow,
  SerializedDatetime,
} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import {Behavior} from 'common/widgets/cohorts-query-builder/models/behaviors/behavior';

export const DatetimeOperatorToDateRangeTypeMapping: {[key in DatetimeOperator]: DateRangeType} = {
  [DatetimeOperator.Relative]: DateRangeType.RelativeAfter,
  [DatetimeOperator.Between]: DateRangeType.Between,
  [DatetimeOperator.On]: DateRangeType.On,
  [DatetimeOperator.After]: DateRangeType.On,
  [DatetimeOperator.RelativeBeforeInPast]: DateRangeType.RelativeAfter,
  [DatetimeOperator.RelativeFuture]: DateRangeType.RelativeAfter,
};

export function convertDateRangeToStagedDatetime(
  dateRange: DateRange,
  dateTimeOperator: DatetimeOperator,
): SerializedDatetime | string | string[] {
  if (!(dateTimeOperator in DatetimeOperatorToDateRangeTypeMapping)) {
    // [shawn] This is never hit if compile time validations are correct but adding for runtime
    throw new Error(`Invalid datetime operator`);
  }

  if (!dateRange.window && !(typeof dateRange.from === `string` || typeof dateRange.to === `string`)) {
    throw new Error(`Unsupported conversion for relative dates in the from property`);
  }

  if (dateRange.window?.unit && !Object.values(BaseTimeUnit).includes(dateRange.window.unit as BaseTimeUnit)) {
    throw new Error(`Unsupported conversion time unit ${dateRange.window.unit}`);
  }

  switch (dateTimeOperator) {
    case DatetimeOperator.After:
    case DatetimeOperator.On:
      return dateRange.from as string;
    case DatetimeOperator.Between:
      return [dateRange.from as string, dateRange.to as string];
    case DatetimeOperator.RelativeFuture:
    case DatetimeOperator.Relative:
    case DatetimeOperator.RelativeBeforeInPast:
      return {
        window: {
          unit: dateRange.window.unit as BaseTimeUnit,
          value: dateRange.window.value,
        },
      };
    default:
      throw new Error(`Unsupported datetime operator conversion`);
  }
}

export function convertSerializedDatetimeToDateRange(
  datetime: SerializedDatetime,
  dateTimeOperator: DatetimeOperator,
): DateRange {
  if (!(dateTimeOperator in DatetimeOperatorToDateRangeTypeMapping)) {
    // [shawn] This is never hit if compile time validations are correct but adding for runtime
    throw new Error(`Invalid datetime operator`);
  }

  let attrs: Partial<DateRange> = {
    type: DatetimeOperatorToDateRangeTypeMapping[dateTimeOperator],
  };

  switch (dateTimeOperator) {
    case DatetimeOperator.After:
    case DatetimeOperator.On:
      attrs.from = datetime.from_date ?? (datetime as string);
      break;
    case DatetimeOperator.Between:
      attrs = {
        ...attrs,
        from: datetime.from_date,
        to: datetime.to_date,
      };
      break;
    // [shawn] Seems like the RelativeFuture is no longer produced by the UI
    // adding the conversion for legacy support
    case DatetimeOperator.RelativeFuture:
    case DatetimeOperator.Relative:
      attrs.window = datetime.window;
      break;
    case DatetimeOperator.RelativeBeforeInPast:
      /** @todo: [shawn] Investigate if any real-world case holds to_date as SerializedBehaviorWindow */
      attrs.window = datetime.window || (datetime.to_date as SerializedBehaviorWindow);
      break;
    default:
      throw new Error(`Unsupported datetime operator conversion`);
  }

  return new DateRange(attrs);
}

export function getDateRangeFromBehavior(behavior: Behavior): DateRange {
  if (behavior.behaviorWindow) {
    return new DateRange({
      type: DateRangeType.RelativeAfter,
      window: behavior.behaviorWindow,
    });
  }

  let type = DateRangeType.Since;

  if (behavior.toDate === behavior.fromDate) {
    type = DateRangeType.On;
  } else if (behavior.toDate) {
    type = DateRangeType.Between;
  }

  return new DateRange({
    type,
    from: behavior.fromDate,
    to: behavior.toDate,
  });
}

export function getDateRangeLabelFromBehavior(behavior: Behavior, negated: boolean): string {
  let connector = `from`;
  if (behavior.behaviorWindow) {
    /** @todo: [shawn] Implement the `within the` logic for funnels
     * when we support funnels cohorts in this format */
    connector = `in the`;
  } else if (behavior.fromDate === behavior.toDate) {
    connector = `on`;
  } else if (!behavior.toDate) {
    connector = `since`;
  }

  if (behavior.count.type === BehaviorCountType.Day && !negated) {
    connector = `on unique days, ${connector}`;
  }

  return `${connector} ${behavior.getDateRangeLabel().toLowerCase()}`;
}
